import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BackendService } from '../../../../core/backend.service';
import { DashboardWidget } from '../../../../viewmodels/dashboard/dashboard-widget';
import { DashboardService } from '../../dashboard.service';
import { ViewDataTableService } from '../view-data-widget/view-data-table.service';
import { WidgetConfigurationDialogComponent } from '../widget-configuration/widget-configuration-dialog.component';
import { WidgetConfigurationService } from '../widget-configuration/widget-configuration-dialog.service';
import { WidgetDataType } from '../widget-data-type';
import { WidgetTableData } from '../widget-table-data';

declare let $: any;

@Component({
  selector: 'hb-base-widget',
  templateUrl: 'base-widget.component.html',
  styleUrls: ['base-widget.component.scss'],
})
export class BaseWidgetComponent implements OnInit, AfterViewInit {
  private unsubscribe: Subject<boolean> = new Subject<boolean>();
  public editingTitle: boolean;
  public hoveringTitle: boolean;

  private currentWidgetTitle: string;
  private _dashboardWidget: DashboardWidget;
  public widgetDataTypeEnum = WidgetDataType;

  public isShowingDataTable: boolean;
  public hasItemizedDataView: boolean;
  public itemizedDataFilterName: string;

  private readonly widgetWithItemizedDataView: WidgetDataType[] = [
    WidgetDataType.RiskDistribution,
    WidgetDataType.OverdueMitigations,
    WidgetDataType.PlannedMaintenanceImprovements,
    WidgetDataType.PlannedMaintenanceDistribution,
    WidgetDataType.CompletedReports,
    WidgetDataType.AssetCount,
    WidgetDataType.MitigationsDue,
    WidgetDataType.HighRiskItems,
    WidgetDataType.MitigationsDueByMonth,
  ];

  @Input() showDataOption: boolean;
  @Input() widgetTableData: WidgetTableData;

  @Input() public title: string;
  @Input() public id: string;
  @Input() public sortable: boolean;
  @Input() showActions: boolean;
  @Input() hideDragAndDrop = false;
  @Input() hideCog = false;
  @Input() hideTitleEdit = false;

  @Input() set dashboardWidget(dbw: DashboardWidget) {
    this._dashboardWidget = dbw;
    this.currentWidgetTitle = dbw?.widget.title;
    this.widgetConfigService.dashboardWidget = this.dashboardWidget;
    this.setHasItemizedDataView();
  }

  get dashboardWidget() {
    return this._dashboardWidget;
  }

  public showInfoBtn: boolean = false;
  public infoTooltipText: string = '';
  private readonly infoTooltipMessages = {
    [WidgetDataType.PlannedMaintenanceImprovements]:
      this.i18NextPipe.transform('Widgets.InfoTooltipMessage'),
  };

  @ViewChild('titleEdit', { static: false }) public titleEdit: ElementRef;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private dashboardService: DashboardService,
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private widgetConfigService: WidgetConfigurationService,
    private backendService: BackendService,
    private viewDataTableService: ViewDataTableService,
    private i18NextPipe: I18NextPipe
  ) { }

  public showConfig() {
    this.widgetConfigService.dashboardWidget = this.dashboardWidget;
    this.bsModalRef = this.modalService.show(WidgetConfigurationDialogComponent);
  }

  public ngOnInit() {
    this.viewDataTableService.setItemizedDataFilterName.pipe(takeUntil(this.unsubscribe)).subscribe(param => {
      this.itemizedDataFilterName = param && param.widgetId === this._dashboardWidget.widget.widgetId ? param.displayName : null;
    });
    this.initInfoButton();
  }

  public ngAfterViewInit(): void {
    const zIndex = 999;
    const draggingZ = 9999;

    let dragParent: any;

    $('.dashboard-card').draggable({
      revert: true,
      revertDuration: 0,
      handle: '.drag-handle',
      start: event => {
        $('#' + event.target.id).css('z-index', draggingZ);
        dragParent = $('#' + event.target.id).parents('.sortable-column');
      },
      drag: (event, ui) => {
        dragParent.find('.drop-zone-wrapper').addClass('target-drop-zone');
        ui.position.left = event.pageX - dragParent[0].offsetLeft - 25;
        ui.position.top = event.pageY - dragParent[0].offsetTop - 20;
      },
      stop: event => {
        $('#' + event.target.id).css('z-index', zIndex);
        dragParent.find('.drop-zone-wrapper').removeClass('target-drop-zone');
      },
    });

    $('.sortable-column').droppable({
      drop: (event, ui) => {
        const draggedWidget = ui.draggable[0];
        const targetWidget = event.target.querySelector('.dashboard-card');
        this.dashboardService.updateWidgetPosition(draggedWidget.id, targetWidget.id);
        this.backendService
          .updateDashboardWidgetPositions(this.dashboardService.dashboardId, this.dashboardService.dashboardWidgets)
          .subscribe(() => { });
      },
      over: (event, ui) => {
        const draggedWidget = ui.draggable[0];
        const targetWidget = event.target.querySelector('.dashboard-card');
        this.dashboardService.updateWidgetPosition(draggedWidget.id, targetWidget.id);
      },
    });
  }

  public toggleEdit() {
    this.editingTitle = !this.editingTitle;
    this.changeDetector.detectChanges();

    if (this.editingTitle) {
      this.hoveringTitle = false;
      this.titleEdit.nativeElement.focus();
    } else {
      if (this.dashboardWidget.widget.title !== this.currentWidgetTitle) {
        this.updateWidget();
      }
    }
  }

  public editTitleFocus() {
    $('#titleEdit' + this.dashboardWidget.dashboardWidgetId).select();
  }

  public editTitleKeyPress(event) {
    if (event.key === 'Enter') {
      $('#titleEdit' + this.dashboardWidget.dashboardWidgetId).blur();
    }
  }

  public updateWidget() {
    this.dashboardService.updateWidget(this.dashboardWidget.widget, false);
  }

  public updateShowData(): void {
    this.isShowingDataTable = !this.isShowingDataTable;
  }

  private setHasItemizedDataView(): void {
    const {
      widget: { widgetDataType },
    } = this._dashboardWidget;
    this.hasItemizedDataView = this.widgetWithItemizedDataView.includes(widgetDataType);
  }

  public showItemized() {
    this.viewDataTableService.showItemizedData.next(this.dashboardWidget.widget);
  }

  public showOverall() {
    this.viewDataTableService.showOverallTable.next(this.dashboardWidget);
    this.widgetTableData.WidgetId = this.dashboardWidget.widget.widgetId;
    this.viewDataTableService.setOverallTableData.next(this.widgetTableData);
  }

  private initInfoButton(): void {
    if (this.dashboardWidget && this.dashboardWidget.widget) {
      const {
        widget: { widgetDataType },
      } = this.dashboardWidget;
      this.showInfoBtn = this.hasInfoBtn(widgetDataType);
      if (this.showInfoBtn) {
        this.infoTooltipText = this.infoTooltipMessages[widgetDataType];
      }
    }
  }

  private hasInfoBtn(widgetDataType: WidgetDataType): boolean {
    return widgetDataType === this.widgetDataTypeEnum.PlannedMaintenanceImprovements;
  }
}
